/**
 * TanStack Query hooks for curriculum resources:
 * CourseUnit, CourseLesson, CourseAssignment.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@client/lib/api-client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export interface CourseUnit {
  id: string;
  courseId: string;
  orderNo: number;
  name: string;
  description?: string | null;
  objectives?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CourseLesson {
  id: string;
  unitId: string;
  orderNo: number;
  title: string;
  description?: string | null;
  content?: string | null;
  learningObjectives?: string | null;
  teacherNotes?: string | null;
  homeworkSummary?: string | null;
  suggestedDurationMin?: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface CourseAssignment {
  id: string;
  unitId: string;
  courseLessonId?: string | null;
  type: 'homework' | 'quiz' | 'project';
  title: string;
  description?: string | null;
  maxScore?: number | null;
  dueDaysAfterLesson?: number | null;
  status: 'draft' | 'published' | 'closed';
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Query Keys
// ---------------------------------------------------------------------------
export const unitKeys = {
  all: ['course-units'] as const,
  byCourse: (courseId: string) => ['course-units', { courseId }] as const,
  detail: (id: string) => ['course-units', 'detail', id] as const,
};

export const lessonKeys = {
  all: ['course-lessons'] as const,
  byUnit: (unitId: string) => ['course-lessons', { unitId }] as const,
  detail: (id: string) => ['course-lessons', 'detail', id] as const,
};

export const assignmentKeys = {
  all: ['course-assignments'] as const,
  byUnit: (unitId: string) => ['course-assignments', { unitId }] as const,
  byLesson: (courseLessonId: string) => ['course-assignments', { courseLessonId }] as const,
  byCourse: (courseId: string) => ['course-assignments', 'by-course', courseId] as const,
};

// ---------------------------------------------------------------------------
// CourseUnit hooks
// ---------------------------------------------------------------------------
export function useCourseUnits(courseId: string) {
  return useQuery({
    queryKey: unitKeys.byCourse(courseId),
    queryFn: () => api.get<CourseUnit[]>('/api/course-units', { courseId, pageSize: 100 }),
    enabled: !!courseId,
  });
}

export function useCourseUnit(id: string) {
  return useQuery({
    queryKey: unitKeys.detail(id),
    queryFn: () => api.get<CourseUnit>(`/api/course-units/${id}`),
    enabled: !!id,
  });
}

export function useCreateCourseUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: unknown) => api.post<CourseUnit>('/api/course-units', data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: unitKeys.all });
    },
  });
}

export function useUpdateCourseUnit(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: unknown) => api.patch<CourseUnit>(`/api/course-units/${id}`, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: unitKeys.all });
    },
  });
}

export function useDeleteCourseUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/course-units/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: unitKeys.all });
    },
  });
}

export function useReorderCourseUnits(courseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: { id: string; orderNo: number }[]) =>
      api.post<void>('/api/course-units/reorder', { items }),
    onMutate: async (items) => {
      await qc.cancelQueries({ queryKey: unitKeys.byCourse(courseId) });
      const prev = qc.getQueryData<CourseUnit[]>(unitKeys.byCourse(courseId));
      if (prev) {
        const updated = prev.map((u) => {
          const found = items.find((i) => i.id === u.id);
          return found ? { ...u, orderNo: found.orderNo } : u;
        });
        qc.setQueryData(unitKeys.byCourse(courseId), [...updated].sort((a, b) => a.orderNo - b.orderNo));
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(unitKeys.byCourse(courseId), ctx.prev);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: unitKeys.byCourse(courseId) });
    },
  });
}

// ---------------------------------------------------------------------------
// CourseLesson hooks
// ---------------------------------------------------------------------------
export function useCourseLesson(id: string) {
  return useQuery({
    queryKey: lessonKeys.detail(id),
    queryFn: () => api.get<CourseLesson>(`/api/course-lessons/${id}`),
    enabled: !!id,
  });
}

export function useCourseLessons(unitId: string) {
  return useQuery({
    queryKey: lessonKeys.byUnit(unitId),
    queryFn: () => api.get<CourseLesson[]>('/api/course-lessons', { unitId, pageSize: 100 }),
    enabled: !!unitId,
  });
}

export function useCreateCourseLesson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: unknown) => api.post<CourseLesson>('/api/course-lessons', data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: lessonKeys.all });
    },
  });
}

export function useUpdateCourseLesson(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: unknown) => api.patch<CourseLesson>(`/api/course-lessons/${id}`, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: lessonKeys.all });
    },
  });
}

export function useDeleteCourseLesson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/course-lessons/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: lessonKeys.all });
    },
  });
}

export function useReorderCourseLessons(unitId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: { id: string; orderNo: number }[]) =>
      api.post<void>('/api/course-lessons/reorder', { items }),
    onMutate: async (items) => {
      await qc.cancelQueries({ queryKey: lessonKeys.byUnit(unitId) });
      const prev = qc.getQueryData<CourseLesson[]>(lessonKeys.byUnit(unitId));
      if (prev) {
        const updated = prev.map((l) => {
          const found = items.find((i) => i.id === l.id);
          return found ? { ...l, orderNo: found.orderNo } : l;
        });
        qc.setQueryData(lessonKeys.byUnit(unitId), [...updated].sort((a, b) => a.orderNo - b.orderNo));
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(lessonKeys.byUnit(unitId), ctx.prev);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: lessonKeys.byUnit(unitId) });
    },
  });
}

// ---------------------------------------------------------------------------
// CourseAssignment hooks
// ---------------------------------------------------------------------------
export function useCourseAssignments(params: { unitId?: string; courseLessonId?: string; courseId?: string }) {
  const key = params.unitId
    ? assignmentKeys.byUnit(params.unitId)
    : params.courseLessonId
      ? assignmentKeys.byLesson(params.courseLessonId)
      : params.courseId
        ? assignmentKeys.byCourse(params.courseId)
        : assignmentKeys.all;

  return useQuery({
    queryKey: key,
    queryFn: () =>
      api.get<CourseAssignment[]>('/api/course-assignments', {
        ...(params.unitId && { unitId: params.unitId }),
        ...(params.courseLessonId && { courseLessonId: params.courseLessonId }),
        pageSize: 100,
      }),
    enabled: !!(params.unitId || params.courseLessonId || params.courseId),
  });
}

export function useCreateCourseAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: unknown) => api.post<CourseAssignment>('/api/course-assignments', data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: assignmentKeys.all });
    },
  });
}

export function useUpdateCourseAssignment(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: unknown) => api.patch<CourseAssignment>(`/api/course-assignments/${id}`, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: assignmentKeys.all });
    },
  });
}

export function useDeleteCourseAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/course-assignments/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: assignmentKeys.all });
    },
  });
}
